/**
 * Story Board API module — the default scene-level view (PR1) plus the
 * per-node structural editing pattern (PR2). See docs brief.
 *
 * client.js attaches Clerk auth automatically and returns the raw
 * `{data: ...}` body; `unwrap` peels one or two `data` layers (mirrors
 * api/corridor.js's convention).
 */
import { api, ApiError, buildAuthHeaders } from './client';
import { parseSseBlocks } from './chat';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const unwrap = (res) => (res?.data?.data ?? res?.data ?? res);

// GET outline-map -> { outline, sections (flat, full columns), logline }.
// Deliberately NOT render-tree (Corridor's data source): render-tree only
// surfaces `content` on true leaf spine nodes (backend/services/
// outline_render.py `_walk` — a scene with beat children always gets
// `content: null` there, regardless of its own row). The Story Board's
// readiness proxy needs each scene's own `description`/`generated_content`/
// `child_count` directly, so it reads the flat section rows and groups them
// itself (see hooks/useStoryBoard.js) rather than consuming a pre-pruned tree.
export async function getOutlineMap(storyId) {
  return unwrap(await api.get(`/stories/${storyId}/outline-map`));
}

// PATCH /sections/{id} — rename only touches display_name.
export async function renameSection(sectionId, displayName) {
  return unwrap(await api.patch(`/sections/${sectionId}`, { display_name: displayName }));
}

// PUT /outline-sections/{id}/reorder?new_order=N — new_order is a query
// param, not a body; the server shifts the rest of the siblings.
export async function reorderSection(sectionId, newOrder) {
  return unwrap(await api.put(`/outline-sections/${sectionId}/reorder?new_order=${newOrder}`));
}

// DELETE /sections/{id}?mode=recursive.
//
// mode=recursive is REQUIRED — this wrapper never exposes a mode param. The
// endpoint's default, mode=orphan, sets children's parent_section_id to NULL
// instead of deleting them, which is exactly the shape that produced the
// orphaned-outline bug (#384: sequences/scenes with a NULL parent, invisible
// to groupSpine). recursive deletes the target and every descendant
// atomically, so a delete can never leave an orphaned spine row behind.
export async function deleteSection(sectionId) {
  return unwrap(await api.delete(`/sections/${sectionId}?mode=recursive`));
}

// POST /sections/{id}/duplicate — deep-copies the section and its
// descendants, inserted as a sibling right after the original. No body.
export async function duplicateScene(sectionId) {
  return unwrap(await api.post(`/sections/${sectionId}/duplicate`));
}

// POST /stories/{storyId}/acts — a blank act; the server computes its
// sequence_order (appends at the end), so no order is sent here.
export async function addAct(storyId) {
  return unwrap(await api.post(`/stories/${storyId}/acts`, { title: 'New Act', description: '' }));
}

// POST /outlines/{outlineId}/sections — add a sequence (parent must be an
// act), a scene (parent must be a sequence), a beat (parent must be a
// scene), or an act (parent null / root — how an accepted act offer is
// written, C2). sequenceOrder is computed by the caller
// (useStoryBoard.addChild / useOfferShelves.acceptOffer) from the
// already-known sibling list. `metadata` and `description` are both
// optional — addChild passes { action: 'New beat' } only for beats;
// accepting an act offer passes `description` (C2); accepting sequence/
// scene/beat offers pass `metadata`.
export async function addChildSection(outlineId, { sectionType, displayName, description, parentSectionId, sequenceOrder, metadata }) {
  return unwrap(await api.post(`/outlines/${outlineId}/sections`, {
    section_type: sectionType,
    display_name: displayName,
    parent_section_id: parentSectionId,
    sequence_order: sequenceOrder,
    ...(description !== undefined ? { description } : {}),
    ...(metadata !== undefined ? { metadata } : {}),
  }));
}

// The outline row (for its id) — accepted offers are written under
// outline_id, which the outline-map response only carries when an outline
// already exists. On a brand-new story (Generate acts from the empty state)
// there may be none yet, so acceptOffer resolves it here first. Mirrors
// api/corridor.js::getOrCreateOutline; corridor.js retires in C6, so this is
// a deliberate re-implementation, not an import.
export async function getOrCreateOutline(storyId) {
  try {
    return unwrap(await api.get(`/stories/${storyId}/outline`));
  } catch (err) {
    if (err.status === 404) {
      return unwrap(await api.post(`/stories/${storyId}/outline`, {}));
    }
    throw err;
  }
}

// ── generation (offers; persists NOTHING until an accept writes a row) ───────
// Same endpoints api/corridor.js calls — re-implemented here, NOT imported,
// because corridor.js retires in C6. Each returns the flat `suggestions`
// array (offer objects: {title|name, ...tier fields}); the offers state
// machine (useOfferShelves) holds them as UI-only until accepted.

// POST /stories/{storyId}/structure/generate-acts?framework_key=... -> acts.
export async function generateActs(storyId, frameworkKey) {
  const qs = frameworkKey
    ? `?framework_key=${encodeURIComponent(frameworkKey)}`
    : '';
  const res = unwrap(await api.post(
    `/stories/${storyId}/structure/generate-acts${qs}`, {}));
  return res?.suggestions || [];
}

// POST /stories/{storyId}/structure/generate-outline — nested act/sequence/
// scene offers. Persists nothing. depth: acts | acts_sequences | all_to_scenes.
// Pass onStep to opt into SSE layer/act progress; JSON without it is unchanged.
export const STORY_BOARD_GENERATION_KEY = 'story_board_generation';

export async function generateOutline(storyId, depth, frameworkKey, { onStep } = {}) {
  const body = {
    depth,
    function_key: STORY_BOARD_GENERATION_KEY,
    ...(frameworkKey !== undefined ? { framework_key: frameworkKey } : {}),
  };
  if (!onStep) {
    return unwrap(await api.post(`/stories/${storyId}/structure/generate-outline`, body));
  }

  const headers = await buildAuthHeaders({ Accept: 'text/event-stream' });
  const response = await fetch(`${API_BASE}/stories/${storyId}/structure/generate-outline`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let data = {};
    const text = await response.text();
    if (text) {
      try { data = JSON.parse(text); } catch { data = {}; }
    }
    throw new ApiError(
      data?.error?.message || data?.detail || 'Generation failed.',
      data?.error?.code,
      response.status,
    );
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream')) {
    const text = await response.text();
    const data = text ? JSON.parse(text) : {};
    return unwrap(data);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parsed = parseSseBlocks(buffer);
    buffer = parsed.rest;
    for (const { event, data } of parsed.events) {
      if (event === 'step') {
        onStep(data);
      } else if (event === 'done') {
        result = data;
      } else if (event === 'error') {
        throw new ApiError(
          data.message || 'Generation failed.',
          data.code || 'LLM_ERROR',
          500,
        );
      }
    }
  }

  if (!result) {
    throw new ApiError('Generation ended without a result.', 'LLM_ERROR', 500);
  }
  return unwrap(result);
}

// POST /outline-sections/{parentId}/generate-children -> sequences | scenes |
// beats for the parent. frameworkKey is only meaningful for sequences; scenes
// and beats ignore it server-side.
export async function generateChildren(parentId, childType, { frameworkKey, maxCount = 5 } = {}) {
  const res = unwrap(await api.post(`/outline-sections/${parentId}/generate-children`, {
    child_type: childType,
    framework_key: frameworkKey,
    max_count: maxCount,
  }));
  return res?.suggestions || [];
}

// POST /stories/{storyId}/regenerate-span -> { offers: [...], constraint_note }.
// The one DESTRUCTIVE generation path (C3): constrained infill of a contiguous
// span of sibling rows between two FIXED boundary rows. Same endpoint
// corridor.js calls, re-implemented here (corridor.js retires in C6). The
// caller's spanRowIds (which rows to dim on accept) is UI-only and is stripped
// by the hook BEFORE calling this — it is never sent to the API.
export async function regenerateSpan(storyId, {
  parentId, childType, leftBoundary = null, rightBoundary = null,
  spanCount = 3, rejectionNote = null,
}) {
  return unwrap(await api.post(`/stories/${storyId}/regenerate-span`, {
    parent_id: parentId,
    child_type: childType,
    left_boundary: leftBoundary,
    right_boundary: rightBoundary,
    span_count: spanCount,
    rejection_note: rejectionNote,
  }));
}

// PATCH /sections/{id} — metadata only. update_section REPLACES metadata
// wholesale (verified against services/outline.py — no jsonb merge), so
// `metadata` here must already be the full merged object; the caller
// (useStoryBoard.updateSceneFields) always does the read-modify-write.
export async function updateSceneFields(sceneId, metadata) {
  return unwrap(await api.patch(`/sections/${sceneId}`, { metadata }));
}

// PATCH /sections/{id} — first-class description (act / sequence).
export async function updateSectionDescription(sectionId, description) {
  return unwrap(await api.patch(`/sections/${sectionId}`, { description }));
}

// PATCH /sections/{id} — a beat's metadata. update_section REPLACES
// metadata wholesale (no jsonb merge), so `metadata` here must already be
// the full merged object; the caller (useStoryBoard.updateBeatAction) does
// the read-modify-write, preserving any legacy who/reaction fields a real
// historical beat carries — their removal is deferred to #377.
export async function updateBeatAction(beatId, metadata) {
  return unwrap(await api.patch(`/sections/${beatId}`, { metadata }));
}

// ── chapters overlay (C4) — a NON-DESTRUCTIVE position overlay: a break
// "after scene X" closes a chapter at X; membership is DERIVED server-side
// (C1's outline-map `chapters` array), never recomputed here. A move is
// clear + commit (there is deliberately no move primitive). Scenes are never
// re-parented. Same endpoints corridor.js calls, re-implemented (corridor.js
// retires in C6).

// POST /stories/{storyId}/chapters/suggest-breaks -> { suggestions: [{
// after_scene_id, after_scene_number, reason }] }. Suggest only, persists
// nothing (LLM).
export async function suggestChapterBreaks(storyId) {
  const res = unwrap(await api.post(`/stories/${storyId}/chapters/suggest-breaks`, {}));
  return res?.suggestions || [];
}

// POST /stories/{storyId}/chapters/commit-break — closes a chapter at
// afterSceneId (null = close the final chapter at the last scene).
export async function commitChapterBreak(storyId, afterSceneId) {
  return unwrap(await api.post(`/stories/${storyId}/chapters/commit-break`, {
    after_scene_id: afterSceneId ?? null,
  }));
}

// DELETE /stories/{storyId}/chapters/{chapterSectionId} — removes the divider;
// its scenes fold into the following chapter (surviving chapters renumber).
export async function clearChapterBreak(storyId, chapterSectionId) {
  return unwrap(await api.delete(`/stories/${storyId}/chapters/${chapterSectionId}`));
}

// ── re-parenting drag + advisory (C5) — the Corridor's remaining capabilities.
// Same endpoints corridor.js calls, re-implemented (corridor.js retires in C6).

// PUT /outlines/{outlineId}/sections/order — bulk reorder within ONE parent;
// positions renumber quietly server-side. Used by a same-parent drag.
export async function reorderSiblings(outlineId, sectionIds, parentSectionId) {
  return unwrap(await api.put(`/outlines/${outlineId}/sections/order`, {
    section_ids: sectionIds,
    parent_section_id: parentSectionId ?? null,
  }));
}

// PATCH /sections/{id} — re-parent (move across parents). Backend owns the
// parentage validation (act->sequence->scene->beat); we never pre-empt it.
export async function moveSection(sectionId, newParentId) {
  return unwrap(await api.patch(`/sections/${sectionId}`, { parent_section_id: newParentId }));
}

// POST /stories/{storyId}/ripple-check — transient advisory + citations,
// persists nothing, never asserts staleness. project_id is derived server-side
// from the owned story (authz-hardened contract, PR #351) — the client NEVER
// sends it.
export async function rippleCheck(storyId, editedRow) {
  return unwrap(await api.post(`/stories/${storyId}/ripple-check`, { edited_row: editedRow }));
}

// GET /stories/{storyId}/scenes/{sceneId}/brief -> { markdown, brief }. The
// "Copy scene brief" export.
export async function getSceneBrief(storyId, sceneId) {
  return unwrap(await api.get(`/stories/${storyId}/scenes/${sceneId}/brief`));
}

// POST /stories/{storyId}/scenes/{sceneId}/regenerate-fields -> {
//   linked_session_count, message_count,
//   draft: null | { setting, intention, characters: [{name, entry_id, status}] },
// }. Read/generate-only (plot-dossier-spec.md "Field handoff") — writes
// NOTHING. `draft` is null when the scene has no linked-session content yet.
// The writer reviews the draft client-side; confirming a field reuses the
// existing updateSceneFields mutation above, not a separate write path.
export async function regenerateSceneFields(storyId, sceneId) {
  return unwrap(await api.post(`/stories/${storyId}/scenes/${sceneId}/regenerate-fields`, {}));
}

const storyBoardApi = {
  getOutlineMap, renameSection, reorderSection, deleteSection,
  duplicateScene, addAct, addChildSection, updateSceneFields, updateBeatAction,
  getOrCreateOutline, generateActs, generateOutline, generateChildren, regenerateSpan,
  updateSectionDescription,
  suggestChapterBreaks, commitChapterBreak, clearChapterBreak,
  reorderSiblings, moveSection, rippleCheck, getSceneBrief, regenerateSceneFields,
};
export default storyBoardApi;
